import { FC } from "react";
import { useTranslation } from "react-i18next";
import { Outlet, useLocation, useNavigate } from "react-router-dom";

import { Button } from "@/components/Interface/Button/Button";
import { StepMenuItem } from "@/components/Interface/Stepper/StepMenu";
import { StepFlowLayout } from "@/components/Layout/BaseLayouts/StepFlowLayout/StepFlowLayout";
import { useQueryParam } from "@/hooks/useQueryParam";
import { useSimIdFromQueryParam } from "@/hooks/useSimIdFromQueryParam";
import { portalRoutes } from "@/router/routes/portalRoutes";

const TOTAL_STEPS = 2;

const STEPS = {
  rateSelection: { step: 1, label: "steps.rate-selection.label" },
  checkout: { step: 2, label: "steps.checkout.label" },
} as const;

/** Shell for the 2-step subscription-change flow. Wraps both steps in StepFlowLayout. */
export const SubscriptionChange: FC = () => {
  const { t } = useTranslation("portal", { keyPrefix: "subscription-change" });
  const { t: tCommon } = useTranslation();
  const navigate = useNavigate();
  const location = useLocation();
  const [simId] = useSimIdFromQueryParam();

  const isCheckoutStep = location.pathname.endsWith("/checkout");
  const currentStep = isCheckoutStep ? STEPS.checkout : STEPS.rateSelection;
  const [billingPeriodSwitchParam] = useQueryParam("billingPeriodSwitch");
  // When the user arrives via the billing-period "Wechseln" shortcut, step 1
  // was skipped intentionally — lock it so they can't wander back into the
  // rate-selection screen and accidentally reset their choice.
  const isBillingPeriodSwitch = billingPeriodSwitchParam === "true";

  const menuItems: StepMenuItem[] = [
    {
      label: t(STEPS.rateSelection.label),
      path: `${portalRoutes.manageDevice.subscription.change}?simId=${simId}`,
      // Disabled on step 1 (already there), on step 2 when billing-period
      // switch is active (back not allowed), enabled otherwise so user can go back.
      disabled: !isCheckoutStep || isBillingPeriodSwitch,
    },
    {
      label: t(STEPS.checkout.label),
      path: `${portalRoutes.manageDevice.subscription.checkout}?simId=${simId}`,
      // Always disabled: either current step (step 2) or not yet reached (step 1).
      disabled: true,
    },
  ];

  const handleCancel = () => {
    navigate(
      `${portalRoutes.manageDevice.subscription.display}?simId=${simId}`,
      { replace: true },
    );
  };

  return (
    <StepFlowLayout
      dataTestid="subscription-change"
      title={t("heading")}
      step={currentStep.step}
      totalSteps={TOTAL_STEPS}
      stepLabel={t(currentStep.label)}
      menuItems={menuItems}
      menuToggleLabel={t("stepMenuLabel")}
      headerAction={
        <Button
          dataTestid="subscription-change-cancel"
          variant="tertiary"
          onClick={handleCancel}
        >
          {tCommon("Common.label.cancel")}
        </Button>
      }
    >
      <Outlet />
    </StepFlowLayout>
  );
};
